import { observer } from 'mobx-react';
import { ChangeEvent, useCallback, useMemo } from 'react';
import { appStore } from '~/mobx/appStore';
import mealsStore from '../../store/mealsStore';
import { defaultIntegrationTooltipOptions } from 'src/constants/integrations';
import integrationsStore from 'src/mobx/integrationStore';
import { localeStore } from 'src/mobx/localesStore';
import { booleanNumberReverse, booleanToNumber, getLastArrayItem, getTranslate, isDisabledByPos, stringTransform } from 'src/utils/helpers';
import { numberValidation } from 'src/utils/validation';
import InputAlt from 'src/view/components/Controls/InputAlt/InputAlt';
import MultiLanguageInput from 'src/view/components/Controls/MultiLanguageInput/MultiLanguageInput';
import MultiLanguageTextArea from 'src/view/components/Controls/MultiLanguageTextArea/MultiLanguageTextArea';
import LazyCategoriesPicker from 'src/view/components/Controls/Pickers/LazyCategoriesPicker/LazyCategoriesPicker';
import { IPickerOption } from 'src/view/components/Controls/Pickers/Picker/types';
import Switcher from 'src/view/components/Controls/Switcher/Switcer';
import IntegrationManager from 'src/view/components/IntegrationManager/IntegrationManager';
import IntegrationTooltip from 'src/view/components/IntegrationTooltip/IntegrationTooltip';
import Text, { TEXT } from 'src/view/components/Text/Text';
import { ProductWarning } from '~/view/components/ProductWarning';
import './style.scss';

const MealMainDetails: React.FC = observer((): JSX.Element | null => {
  const selectCategory = useCallback((options: IPickerOption[]) => {
    const option: IPickerOption | undefined = getLastArrayItem<IPickerOption>(options);
    mealsStore.changeMealHandler({
      category: option ? { id: Number(option.value), name: { [localeStore.language]: option.label }, status: option.status } : undefined,
    });
    // update validation
    if (!mealsStore.validation.category) mealsStore.setValidation();
  }, []);

  const changeSortNumber = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (!numberValidation(value)) return;
    mealsStore.changeMealHandler({ sort: stringTransform(value) });
  }, []);

  const selectedCategories = useMemo((): IPickerOption[] | undefined => {
    if (!mealsStore.meal?.category) return undefined;

    return [{ label: getTranslate(mealsStore.meal.category.name), value: mealsStore.meal.category.id }];
  }, [mealsStore.meal?.category]);

  if (!mealsStore.meal) return null;

  const {
    comboMealName: nameRestrictionAccess,
    comboMealDescription: descRestrictionAccess,
    comboCategories: categoriesRestrictionAccess,
    comboMealStatus: availabilityRestrictionAccess,
    globalItemStock: stockRestrictionAccess,
  } = integrationsStore.posRestrictions || {};

  return (
    <div className="d-flex flex-column mt-4">
      {/* switchers */}
      <div className="d-flex flex-row">
        <IntegrationManager setting={availabilityRestrictionAccess} wrapperClassName="d-flex flex-row align-items-center mr-8">
          <IntegrationTooltip isVisible={isDisabledByPos(availabilityRestrictionAccess)}>
            <Switcher status={mealsStore.meal?.status || 0} onChange={(status) => mealsStore.changeMealHandler({ status })} />
          </IntegrationTooltip>
          <Text type={TEXT.SWITCHER_LABEL} text="availability" disabled={isDisabledByPos(availabilityRestrictionAccess)} />
        </IntegrationManager>
        <IntegrationManager setting={stockRestrictionAccess} wrapperClassName="d-flex flex-row align-items-center mr-8">
          <IntegrationTooltip isVisible={isDisabledByPos(stockRestrictionAccess)}>
            <Switcher
              status={booleanNumberReverse(mealsStore.meal?.out_of_stock)}
              onChange={(stock) => mealsStore.changeMealHandler({ out_of_stock: booleanNumberReverse(stock) })}
            />
          </IntegrationTooltip>
          <Text type={TEXT.SWITCHER_LABEL} text="in_stock" disabled={isDisabledByPos(stockRestrictionAccess)} />
        </IntegrationManager>
        <div className="d-flex flex-row align-items-center mr-8">
          <Switcher
            disabled={!appStore.isAddonsAvailable.promoCode}
            status={booleanToNumber(mealsStore.meal?.promocode_applicable)}
            onChange={(promocode_applicable) => mealsStore.changeMealHandler({ promocode_applicable: booleanToNumber(promocode_applicable) })}
          />
          <Text type={TEXT.SWITCHER_LABEL} text="promocode_applicable" />
        </div>
        <div className="d-flex flex-row align-items-center">
          <Switcher
            disabled={!appStore.isAddonsAvailable.cashback}
            status={booleanToNumber(mealsStore.meal?.cashback_applicable)}
            onChange={(cashback_applicable) => mealsStore.changeMealHandler({ cashback_applicable: booleanToNumber(cashback_applicable) })}
          />
          <Text type={TEXT.SWITCHER_LABEL} text="cashback_applicable" />
        </div>
      </div>
      {/* main details */}
      <Text type={TEXT.SECTION_HEADER} text="main_details" className="mt-6" />
      <div className="d-flex flex-row mt-1">
        <div className="d-flex flex-column mr-4">
          {/* name */}
          <IntegrationManager setting={nameRestrictionAccess} wrapperClassName="d-flex flex-column">
            <Text
              type={TEXT.BLOCK_HEADER}
              text="combo_meal_name"
              className="meals-main-details__name-title mb-2"
              disabled={isDisabledByPos(nameRestrictionAccess)}
              required
            />
            <MultiLanguageInput
              maxLength={75}
              enText={mealsStore.meal.name.en}
              arText={mealsStore.meal.name.ar}
              onChangeValue={mealsStore.changeMealName}
              validation={mealsStore.validation.name}
              tooltip={isDisabledByPos(nameRestrictionAccess) ? { ...defaultIntegrationTooltipOptions, wrapperClassName: 'full-height' } : undefined}
              disabled={isDisabledByPos(nameRestrictionAccess)}
            />
          </IntegrationManager>
          {/* category */}
          <IntegrationManager setting={categoriesRestrictionAccess} wrapperClassName="d-flex flex-column mt-5">
            <div className="d-flex flex-row">
              <Text type={TEXT.BLOCK_HEADER} text="category" className="mb-2" disabled={isDisabledByPos(categoriesRestrictionAccess)} required />
              <ProductWarning type="categoryInEditPage" product={mealsStore.meal} iconSize={21} marginLeft={2} />
            </div>
            <IntegrationTooltip isVisible={isDisabledByPos(categoriesRestrictionAccess)}>
              <LazyCategoriesPicker
                searchable
                markUnavailable
                validation={{ isValid: mealsStore.validation.category, errorText: mealsStore.validation.categoryErrorText }}
                selectedOptions={selectedCategories}
                onSelect={selectCategory}
                className="meals-main-details__input"
              />
            </IntegrationTooltip>
          </IntegrationManager>
        </div>
        {/* descriptions */}
        <IntegrationManager setting={descRestrictionAccess}>
          <MultiLanguageTextArea
            title="description"
            enText={mealsStore.meal.description?.en}
            arText={mealsStore.meal.description?.ar}
            onChangeValue={mealsStore.changeMealDescription}
            tooltip={isDisabledByPos(descRestrictionAccess) ? defaultIntegrationTooltipOptions : undefined}
            disabled={isDisabledByPos(descRestrictionAccess)}
            className="meals-main-details__text-area"
          />
        </IntegrationManager>
      </div>
      <div className="d-flex flex-column mt-5">
        {/* sort */}
        <Text type={TEXT.BLOCK_HEADER} text="sort" />
        <InputAlt className="meals-main-details__input py-0 mt-2" value={mealsStore.meal.sort} onChange={changeSortNumber} />
      </div>
    </div>
  );
});

export default MealMainDetails;
